import styled from '@emotion/styled'
import { KeyboardEvent, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

type TextInputStylingProps = {
  fontSize?: string
  height?: string
  hideBorder?: boolean
  isError?: boolean
  padding?: string
  width?: string
}

export interface TextInputProps extends TextInputStylingProps {
  'data-testid'?: string
  debounce?: boolean
  maxLength?: number
  onChange?: (e: any) => void
  onDebouncedValue?: (updatedValue: string | number) => void
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
  placeholder?: string
  readOnly?: boolean
  type?: string
  value: string | number
}

const StyledTextInput = styled.input<TextInputStylingProps>`
  font-size: ${props => (props.fontSize ? props.fontSize : '13px')};
  width: ${props => (props.width ? props.width : '100%')};
  ${props => (props.height ? `height: ${props.height}` : '')};
  padding: ${props => (props.padding ? props.padding : '12px 8px')};
  margin: 0;
  box-sizing: border-box;
  color: var(--Graphite);

  border: ${props => (props.hideBorder ? 'none' : 'solid 1px var(--White80)')};
  background: ${({ hideBorder, isError }) =>
    // eslint-disable-next-line no-nested-ternary
    isError ? 'var(--WarnBG)' : hideBorder ? 'var(--White95)' : 'var(--White)'};
  vertical-align: middle;
  color: ${props => (props.isError ? 'var(--Graphite)' : 'var(--Graphite)')};

  &:focus-visible {
    outline: none;
  }
  &::placeholder {
    color: var(--White60);
  }
  &:disabled {
    background: var(--White96);
    color: var(--White40);
  }
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

export const TextInput = ({
  fontSize,
  height,
  hideBorder,
  isError,
  maxLength,
  onChange,
  onDebouncedValue,
  onKeyDown,
  padding,
  placeholder,
  readOnly,
  type,
  value,
  width,
  ...props
}: TextInputProps) => {
  const inputType = type || 'text'
  const [localValue, setLocalValue] = useState<string | number>(value)

  const [debounce] = useDebounce(localValue, 1000)
  const localDebouncedValue = onDebouncedValue ? debounce : undefined
  useEffect(() => {
    if (onDebouncedValue && value !== localDebouncedValue) {
      onDebouncedValue(localDebouncedValue)
    }
  }, [localDebouncedValue, onDebouncedValue, value])
  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const onTextChange = e => {
    setLocalValue(e.target.value)
    if (onChange) {
      onChange(e)
    }
  }

  return (
    <StyledTextInput
      data-testid={props['data-testid']}
      fontSize={fontSize}
      height={height}
      hideBorder={hideBorder}
      inputMode={inputType === 'number' ? 'numeric' : 'text'}
      isError={isError}
      maxLength={maxLength}
      padding={padding}
      placeholder={placeholder}
      readOnly={readOnly}
      type={inputType}
      value={localValue}
      width={width}
      onChange={onTextChange}
      onKeyDown={onKeyDown}
    />
  )
}
